/**
 * toggle_flow tool - Enable or disable a flow
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const toggleFlowInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID) to enable or disable"),
  action: z
    .enum(["start", "stop"])
    .describe("Action to perform: 'start' to enable, 'stop' to disable"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
});

export type ToggleFlowInput = z.infer<typeof toggleFlowInputSchema>;

export const toggleFlowTool = {
  name: "toggle_flow",
  description:
    "Enable or disable a Power Automate flow. Use 'start' to enable a stopped flow or 'stop' to disable a running flow. The flow must exist and you must have permission to modify it.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID) to enable or disable",
      },
      action: {
        type: "string",
        enum: ["start", "stop"],
        description: "Action to perform: 'start' to enable, 'stop' to disable",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
    },
    required: ["flowId", "action"],
  },
};

export interface ToggleFlowResult {
  flowId: string;
  action: "start" | "stop";
  success: boolean;
  newState: string;
  message: string;
}

export async function handleToggleFlow(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  const parsed = toggleFlowInputSchema.parse(input);

  logger.info({ flowId: parsed.flowId, action: parsed.action }, "Executing toggle_flow");

  // Get current flow state first
  const flowBefore = await api.getFlow(parsed.flowId, parsed.environment);
  const stateBefore = flowBefore.properties.state;

  const flowName = flowBefore.properties.displayName;

  // Check if action is needed
  if (parsed.action === "start" && stateBefore === "Started") {
    return `"${flowName}" already started`;
  }

  if (parsed.action === "stop" && stateBefore === "Stopped") {
    return `"${flowName}" already stopped`;
  }

  // Perform the action
  if (parsed.action === "start") {
    await api.startFlow(parsed.flowId, parsed.environment);
  } else {
    await api.stopFlow(parsed.flowId, parsed.environment);
  }

  // Verify the new state
  const flowAfter = await api.getFlow(parsed.flowId, parsed.environment);
  const stateAfter = flowAfter.properties.state;

  const expectedState = parsed.action === "start" ? "Started" : "Stopped";
  const verb = parsed.action === "start" ? "Started" : "Stopped";

  if (stateAfter === expectedState) {
    return `${verb} "${flowName}"`;
  }
  return `Warning: "${flowName}" is ${stateAfter} (expected ${expectedState})`;
}
